import { useEffect, useState } from 'react';
import { logo } from '../lib/logo';
import { Link } from 'react-router-dom';
import { ErrorLine } from '../components/ErrorLine';
import { LoadingLine } from '../components/LoadingLine';
import { PageBanner } from '../components/PageBanner';
import { Select } from '../components/Select';
import { api, fmtSigned, type CoachComparison, type CoachSide, type League, type LeaderboardRow } from '../lib/api';

/**
 * Two coaches, same season, same rules.
 *
 * The leaderboard already ranks everyone, but nobody argues about a table. They argue
 * about one man against one other man, and this is the page that settles it.
 */

function Side({ name, side, meta, better, league, season }: {
  name: string; side: CoachSide; meta?: LeaderboardRow; better: boolean;
  league: League; season: number | null;
}) {
  const rows: [string, string][] = [
    ['Games', String(side.games)],
    ['Calls graded', String(side.decisions)],
    ['Optimal', side.optimal_rate === null ? '' : `${(side.optimal_rate * 100).toFixed(0)}%`],
    ['Win probability per game', fmtSigned(side.wp_impact_per_game_pct, 2, '%')],
    ['Wrong', String(side.wrong)],
    ['Questionable', String(side.questionable)],
  ];
  return (
    <div className={`card p-5 ${better ? 'ring-1 ring-blue' : ''}`}>
      <div className="flex items-center gap-3">
        {meta?.team_logo && <img src={logo(meta.team_logo, 36)} alt="" className="h-9 w-9 object-contain" loading="lazy" />}
        <div className="min-w-0">
          <Link to={`/coach/${encodeURIComponent(name)}?league=${league}&season=${season ?? ''}`}
                className="truncate font-display text-[18px] font-bold leading-tight hover:text-blue">{name}</Link>
          <p className="meta truncate !text-[11px]">{meta?.team_name ?? meta?.team ?? ''}</p>
        </div>
      </div>
      {better && <p className="kicker mt-3 !text-blue">Better managed</p>}
      <dl className="mt-4 divide-y divide-line">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-4 py-2">
            <dt className="text-[13px] text-muted">{label}</dt>
            <dd className="font-display text-[17px] font-bold tabular-nums">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function ComparePage() {
  const [league, setLeague] = useState<League>('nfl');
  // The explicit pick, kept apart from what the API resolved: writing the response back
  // into the effect's dependency refetches and blanks what just arrived.
  const [season, setSeason] = useState<number | undefined>(undefined);
  const [resolved, setResolved] = useState<number | null>(null);
  const [seasons, setSeasons] = useState<number[]>([]);
  const [roster, setRoster] = useState<LeaderboardRow[]>([]);
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [data, setData] = useState<CoachComparison | null>(null);
  const [error, setError] = useState<string | null>(null);

  // who is available, and a first argument to open on: the best managed season against
  // the worst is the one people already have
  useEffect(() => {
    let live = true;
    setRoster([]); setData(null); setError(null);
    api.leaderboard(season, 'head_coach', league)
      .then((r) => {
        if (!live) return;
        setRoster(r.coaches); setSeasons(r.seasons); setResolved(r.season);
        setFirst(r.coaches[0]?.coach ?? '');
        setSecond(r.coaches[r.coaches.length - 1]?.coach ?? '');
      })
      .catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [league, season]);

  const load = () => {
    if (!first || !second || first === second) { setData(null); return Promise.resolve(); }
    setError(null); setData(null);
    return api.coachCompare(first, second, league, season ?? resolved ?? undefined)
      .then(setData)
      .catch((e: Error) => setError(e.message));
  };
  useEffect(() => { void load(); }, [first, second, league, resolved]); // eslint-disable-line react-hooks/exhaustive-deps

  const picker = (value: string, onChange: (v: string) => void, label: string) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}
            className="h-10 min-w-0 flex-1 border border-ink/25 bg-paper px-3 font-cond text-[15px] font-semibold uppercase tracking-[0.06em] text-ink outline-none focus:border-blue">
      {roster.map((r) => <option key={r.coach} value={r.coach}>{r.coach}</option>)}
    </select>
  );

  const gap = data?.gap_per_game_pct;

  return (
    <>
      <PageBanner>
        <div>
            <p className="kicker !text-paper/80">Coach grades</p>
            <h1 className="h1 mt-1">Who manages a game better?</h1>
            <p className="mt-3 max-w-[54ch] text-[15px] text-paper/90">
              Both men judged on the same fourth downs, two-point tries, kicks and timeouts,
              against what usually works in that exact situation. How the plays turned out
              does not count.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <div className="seg seg-invert">
                {([['nfl', 'NFL'], ['cfb', 'College']] as const).map(([id, label]) => (
                  <button key={id} onClick={() => { setLeague(id); setSeason(undefined); }}
                          className={`seg-item ${league === id ? 'seg-item-on' : ''}`}>{label}</button>
                ))}
              </div>
              {seasons.length > 1 && (
                <Select label="Season" tone="paper" value={season ?? resolved ?? ''}
                        onChange={(v) => setSeason(Number(v))}
                        options={[...seasons].reverse().map((y) => [y, `${y} season`] as const)} />
              )}
            </div>
        </div>
      </PageBanner>

      <section className="section">
        <div className="container stack">
          {roster.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              {picker(first, setFirst, 'First coach')}
              <span className="label px-1">vs</span>
              {picker(second, setSecond, 'Second coach')}
            </div>
          )}

          {first && second && first === second && (
            <p className="body-muted">Pick two different coaches.</p>
          )}
          {error && <ErrorLine onRetry={load} />}
          {!error && !data && first !== second && <LoadingLine active />}

          {data && (
            <>
              <div className="grid gap-4 md:grid-cols-2">
                {Object.entries(data.coaches).map(([name, side]) => (
                  <Side key={name} name={name} side={side} better={data.better === name}
                        meta={roster.find((r) => r.coach === name)} league={league}
                        season={data.season} />
                ))}
              </div>
              <p className="border-l-2 border-blue bg-blueTint px-3 py-2 text-[14px]">
                {gap === undefined || gap === 0 || !data.better
                  ? 'Neither man cost his team measurably more than the other.'
                  : `${data.better} gave away ${Math.abs(gap).toFixed(2)} percentage points less win probability per game.`}
              </p>
            </>
          )}

          <p className="body-muted max-w-[62ch] text-[13px]">
            Only game management counts here, since that is the head coach's own call. Play
            calling and defence belong to his staff, and public data does not name them. A
            coach with few graded games is held closer to the average of his peers.
          </p>
        </div>
      </section>
    </>
  );
}
